import { useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import type { RemoteNode } from '../lib/api';
import PresenceDot from './PresenceDot';

interface Props {
  nodes: RemoteNode[];
  loading: boolean;
  loadError: string | null;
  onlineById: Record<string, boolean>;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
  onCreateNode: (machineName: string) => Promise<void>;
}

export default function ComputerSidebar({
  nodes,
  loading,
  loadError,
  onlineById,
  selectedNodeId,
  onSelectNode,
  onCreateNode,
}: Props) {
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = async () => {
    const machineName = name.trim();
    if (!machineName || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      await onCreateNode(machineName);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Could not add this computer.');
      return;
    } finally {
      setCreating(false);
    }
    setName('');
    setShowCreate(false);
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <span className="sidebar-title">My Computers</span>
        <div className="sidebar-actions">
          <button
            className="icon-btn"
            title="Add Computer"
            onClick={() => setShowCreate(true)}
          >
            <PlusOutlined />
          </button>
        </div>
      </div>

      {showCreate && (
        <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input
            className="input-field"
            placeholder="Computer name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm btn-primary" onClick={handleCreate} disabled={!name.trim() || creating}>
              {creating ? 'Adding...' : 'Create'}
            </button>
            <button className="btn btn-sm" onClick={() => { setShowCreate(false); setName(''); setCreateError(null); }}>Cancel</button>
          </div>
          {createError && <p className="node-create-error remote-error">{createError}</p>}
        </div>
      )}

      <div className="channel-list">
        {loading ? (
          <div className="sidebar-empty">Loading...</div>
        ) : loadError ? (
          <div className="sidebar-empty node-list-error remote-error">{loadError}</div>
        ) : nodes.length === 0 ? (
          <div className="sidebar-empty">No computers yet</div>
        ) : (
          nodes.map(node => (
            <button
              key={node.id}
              className={`channel-item${selectedNodeId === node.id ? ' channel-item-active' : ''}`}
              onClick={() => onSelectNode(node.id)}
            >
              <span className="channel-name">{node.machine_name}</span>
              <PresenceDot state={onlineById[node.id] ? 'online' : 'offline'} reason={undefined} compact />
            </button>
          ))
        )}
      </div>
    </div>
  );
}
